import { toNum, type Numeric } from '@/utils/numeric';
import type { SprayTankLoad, TankRateStatus } from '@/types/spray';

/**
 * Reading a per-tank water rate check.
 *
 * The server does the judging — water ÷ ground covered, against what the
 * sprayer's calibration says it should put out over that ground. This turns
 * its verdict into something worth showing an operator standing at the fill
 * point with the next load to mix.
 *
 * All of it is advisory. Nothing here blocks anything, because the inputs are
 * a GPS-derived area with real noise in it and a warning that cries wolf at
 * somebody holding a spray hose is ignored by the second tank.
 */

/** How loudly to say it. */
export type TankRateTone = 'ok' | 'watch' | 'alert' | 'unknown';

/**
 * Past this the drift is worth stopping for rather than noting. Under it, a
 * GPS-derived area and a real-world pass explain most of the gap on their own.
 */
const ALERT_VARIANCE_PCT = 25;

export interface TankRateReading {
  status: TankRateStatus;
  tone: TankRateTone;
  applied: number | null;
  expected: number | null;
  variancePct: number | null;
  /** The figures, in one line. */
  headline: string;
  /** What to do about it, or why it could not be judged. Null when all is well. */
  detail: string | null;
}

/** The loads a check can be read off: closed, and judged one way or another. */
type Checked = Pick<
  SprayTankLoad,
  'applied_rate_lpha' | 'expected_rate_lpha' | 'rate_variance_pct'
  | 'rate_check_status' | 'rate_check_reason'
>;

function tone(status: TankRateStatus, variancePct: number | null): TankRateTone {
  if (status === 'ok') return 'ok';
  if (status !== 'high' && status !== 'low') return 'unknown';
  // A judged load with no variance figure should not exist, but reading one as
  // "fine" would be the wrong way to be wrong.
  if (variancePct == null) return 'watch';
  return Math.abs(variancePct) >= ALERT_VARIANCE_PCT ? 'alert' : 'watch';
}

function lpha(value: Numeric | undefined): string {
  const n = toNum(value);
  return n == null ? '—' : `${Math.round(n)} L/ha`;
}

/**
 * What this load's check has to say, or null when there is nothing to say yet.
 *
 * A missing status means the load is still open or predates the check, which
 * is NOT the same as having been looked at and found wanting — so it renders
 * nothing at all rather than an empty verdict.
 */
export function readTankRate(tank: Checked): TankRateReading | null {
  const status = tank.rate_check_status;
  if (!status) return null;

  const applied = toNum(tank.applied_rate_lpha);
  const expected = toNum(tank.expected_rate_lpha);
  const variancePct = toNum(tank.rate_variance_pct);

  if (status === 'unverifiable') {
    return {
      status,
      tone: 'unknown',
      applied,
      expected,
      variancePct,
      headline: applied != null
        ? `${lpha(applied)} — not checked against the calibration`
        : 'Water rate could not be checked',
      detail: tank.rate_check_reason ?? null,
    };
  }

  const t = tone(status, variancePct);
  const drift = variancePct != null
    ? `${variancePct > 0 ? '+' : ''}${variancePct.toFixed(0)}%`
    : null;

  if (status === 'ok') {
    return {
      status, tone: t, applied, expected, variancePct,
      headline: `${lpha(applied)} — matches the calibration${drift ? ` (${drift})` : ''}`,
      detail: null,
    };
  }

  return {
    status,
    tone: t,
    applied,
    expected,
    variancePct,
    headline: `${lpha(applied)} against ${lpha(expected)} calibrated${drift ? ` (${drift})` : ''}`,
    detail: status === 'high'
      ? 'More water went out than the calibration predicts — a slower ground speed, higher pressure or worn nozzles all read like this.'
      : 'Less water went out than the calibration predicts — a blocked nozzle, a clogged filter or a faster ground speed all read like this.',
  };
}

/**
 * The most recent load that was actually judged.
 *
 * Loads that came back `unverifiable` are stepped over rather than treated as
 * clean: a tank nobody could check says nothing about whether the last problem
 * was fixed, and letting it clear a warning would hide a sprayer that is still
 * out. An `ok` load DOES clear it — that is the operator having sorted it.
 */
export function latestJudgedLoad(tanks: SprayTankLoad[]): SprayTankLoad | null {
  const judged = tanks.filter(
    t => t.finished_at
      && (t.rate_check_status === 'ok'
        || t.rate_check_status === 'high'
        || t.rate_check_status === 'low')
  );
  if (!judged.length) return null;
  return judged.reduce((latest, t) => (t.tank_number > latest.tank_number ? t : latest));
}
